import { supabase } from './supabase';
import * as bcrypt from 'bcryptjs';

export interface User {
  id: string;
  email: string;
  is_admin: boolean;
  is_merchant: boolean;
  is_online: boolean;
  wallet_balance: number;
  profile_image: string | null;
  full_name: string | null;
}

export async function loginUser(email: string, password: string): Promise<User> {
  try {
    // Get user from stripeusers table
    const { data: users, error } = await supabase
      .from('stripeusers')
      .select('*')
      .eq('email', email.toLowerCase());

    if (error) {
      throw new Error(handleSupabaseError(error));
    }
    
    if (!users || users.length === 0) {
      throw new Error('User not found');
    }

    const user = users[0];
    const isValid = await bcrypt.compare(password, user.password_hash);
    if (!isValid) {
      throw new Error('Invalid password');
    }

    // Update online status
    const { error: updateError } = await supabase
      .from('stripeusers')
      .update({ is_online: true })
      .eq('id', user.id);

    if (updateError) {
      console.error('Failed to update online status:', updateError);
      // Don't throw here as login was successful
    }

    // Remove password_hash from user object before returning
    const { password_hash, ...userWithoutPassword } = user;
    
    // Store user in localStorage
    localStorage.setItem('user', JSON.stringify(userWithoutPassword));
    
    return userWithoutPassword as User;
  } catch (error: any) {
    throw new Error(error.message || 'Login failed');
  }
}

export async function handleGoogleLogin(): Promise<User | null> {
  try {
    const { data: authData, error: authError } = await supabase.auth.getSession();
    
    if (authError) throw authError;
    if (!authData.session) return null;
    
    const { user: authUser } = authData.session;
    
    if (!authUser || !authUser.email) {
      throw new Error('No user data available from Google login');
    }
    
    console.log('Google auth successful, processing user data');
    
    // Check if user exists in stripeusers table
    const { data: existingUsers, error: existingError } = await supabase
      .from('stripeusers')
      .select('*')
      .eq('email', authUser.email.toLowerCase());
      
    if (existingError) throw existingError;
    
    let stripeUser;
    
    if (!existingUsers || existingUsers.length === 0) {
      console.log('Creating new user in stripeusers table');
      // Create new user in stripeusers table
      const { data: newUser, error: createError } = await supabase
        .from('stripeusers')
        .insert([
          {
            email: authUser.email.toLowerCase(),
            password_hash: '', // No password for OAuth users
            is_online: true,
            wallet_balance: 0,
            is_admin: false,
            is_merchant: false,
            profile_image: authUser.user_metadata?.avatar_url || null,
            full_name: authUser.user_metadata?.full_name || null
          }
        ])
        .select()
        .single();
        
      if (createError) throw createError;
      stripeUser = newUser;
    } else {
      console.log('User already exists in stripeusers table');
      // Update existing user
      stripeUser = existingUsers[0];
      
      // Update online status and profile image if needed
      const { error: updateError } = await supabase
        .from('stripeusers')
        .update({ 
          is_online: true,
          profile_image: authUser.user_metadata?.avatar_url || stripeUser.profile_image,
          full_name: authUser.user_metadata?.full_name || stripeUser.full_name
        })
        .eq('id', stripeUser.id);
        
      if (updateError) {
        console.error('Failed to update user data:', updateError);
      }
    }
    
    // Remove password_hash from user object before returning
    const { password_hash, ...userWithoutPassword } = stripeUser;
    
    console.log('Storing user in localStorage and redirecting');
    // Store user in localStorage
    localStorage.setItem('user', JSON.stringify(userWithoutPassword));
    
    return userWithoutPassword as User;
  } catch (error: any) {
    console.error('Google login error:', error);
    throw new Error(error.message || 'Google login failed');
  }
}

export async function registerUser(email: string, password: string): Promise<User> {
  try {
    // Check if user already exists
    const { data: existingUsers } = await supabase
      .from('stripeusers')
      .select('id')
      .eq('email', email.toLowerCase());

    if (existingUsers && existingUsers.length > 0) {
      throw new Error('Email already registered');
    }

    const hashedPassword = await bcrypt.hash(password, 10);

    // Create user in stripeusers table
    const { data: userData, error: userError } = await supabase
      .from('stripeusers')
      .insert([
        {
          email: email.toLowerCase(),
          password_hash: hashedPassword,
          is_online: true,
          wallet_balance: 0,
          is_admin: false,
          is_merchant: false,
          profile_image: null
        }
      ])
      .select()
      .single();

    if (userError) throw new Error('Failed to create user');
    if (!userData) throw new Error('Failed to create user');

    // Remove password_hash from user object before returning
    const { password_hash, ...userWithoutPassword } = userData;
    return userWithoutPassword as User;
  } catch (error: any) {
    throw new Error(error.message || 'Registration failed');
  }
}

// lib/auth.ts

export async function logoutUser(userId: string) {
  try {
    if (!userId) throw new Error('User ID is required');

    // Mark offline in your table (keep as-is)
    const { error } = await supabase
      .from('stripeusers')
      .update({ is_online: false })
      .eq('id', userId);
    if (error) console.warn('Failed to update online flag:', error);

    // 🔑 IMPORTANT: end Supabase session so pages won't auto-log back in
    await supabase.auth.signOut();

    // Local caches
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
  } catch (err: any) {
    throw new Error(err.message || 'Logout failed');
  }
}


export function getCurrentUser(): User | null {
  const userJson = localStorage.getItem('user');
  if (!userJson) return null;
  
  try {
    return JSON.parse(userJson);
  } catch {
    return null;
  }
}

// Helper function to handle Supabase errors
function handleSupabaseError(error: any) {
  if (error.code === 'PGRST116') {
    return 'No data found';
  }
  
  if (error.code === '23505') {
    return 'This record already exists';
  }
  
  if (error.code === '42501') {
    return 'You do not have permission to perform this action';
  }
  
  return error.message || 'An unexpected error occurred';
}